import React, {FC, useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Loader} from '@optimaxdev/design-system';
import classNames from 'classnames';

import {useFeature} from 'components/feature';
import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {
    CHANGE_ADDRESS_BUTTON_ID,
    CHANGE_PAYMENT_BUTTON_ID,
} from 'desktop/pages/checkout/constants/amazon';
import {AmazonSessionResponseType} from 'types/amazonType';

import {bindAmazonButtons} from '../../libs/bindAmazonButtons';
import {getFirstLastNames} from '../../libs/getFirstLastNames';

import s from './amazonData.scss';

export type PropsType = {
    accessToken: string;
    shippingAddress?: AmazonSessionResponseType['shippingAddress'];
    paymentPreferences?: AmazonSessionResponseType['paymentPreferences'];
    isPaymentConfirmed: boolean;
};

/**
 *  Component for displaying shipping address and payment method
 *  @params {PropsType} props
 *  @returns {React.FC} - component
 */
export const AmazonData: FC<PropsType> = React.memo(
    ({accessToken, isPaymentConfirmed, paymentPreferences, shippingAddress}) => {
        const isFacelift = useFeature('dCheckoutFacelift');

        const handleEditShippingAddress = () => {
            sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
                message: 'Amazon - Shipping address - Edit',
                method: 'Payment',
            });
        };

        const handleEditPaymentMethod = () => {
            sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
                message: 'Amazon - Payment method - Edit',
                method: 'Payment',
            });
        };

        useEffect(() => {
            if (shippingAddress) bindAmazonButtons(accessToken);
        }, [shippingAddress, accessToken]);

        const buttonClasses = classNames({
            [s.button]: true,
            [s.buttonFacelift]: isFacelift,
            [s.buttonHide]: isPaymentConfirmed,
        });

        return shippingAddress ? (
            <ul className={classNames(s.list, {[s.listFacelift]: isFacelift})}>
                <li className={classNames(s.item, {[s.itemAddressFacelift]: isFacelift})}>
                    <h3>Address book</h3>
                    <strong className={s.text}>
                        {`${Object.values(
                            getFirstLastNames(
                                shippingAddress.givenName,
                                shippingAddress.familyName,
                            ),
                        ).join(' ')}`}
                    </strong>
                    <p className={s.text}>
                        {shippingAddress.streetAddress}
                        {shippingAddress.streetAddress2 !== '' && (
                            <>, {shippingAddress.streetAddress2}</>
                        )}
                    </p>
                    <p className={s.text}>
                        {shippingAddress.city && shippingAddress.city}{' '}
                        {shippingAddress.region && shippingAddress.region},{' '}
                        {shippingAddress.postalCode}
                    </p>
                    <p className={s.text}>{shippingAddress.country && shippingAddress.country}</p>
                    <button
                        className={buttonClasses}
                        id={CHANGE_ADDRESS_BUTTON_ID}
                        onClick={handleEditShippingAddress}
                        type="button"
                    >
                        Change
                    </button>
                </li>
                {paymentPreferences && paymentPreferences[0].paymentDescriptor && (
                    <li className={classNames(s.item, {[s.itemPaymentFacelift]: isFacelift})}>
                        <h3>Payment method</h3>
                        <p className={s.text}>{paymentPreferences[0].paymentDescriptor}</p>
                        <button
                            className={buttonClasses}
                            id={CHANGE_PAYMENT_BUTTON_ID}
                            onClick={handleEditPaymentMethod}
                            type="button"
                        >
                            Change
                        </button>
                    </li>
                )}
            </ul>
        ) : (
            <div className={s.loader}>
                <Loader />
            </div>
        );
    },
);

AmazonData.displayName = 'AmazonData';
